import { useEffect, useSyncExternalStore } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { UserType } from "@/shared/types/user.entity";

import { CryptoSwapsService } from "../services/cryptoSwaps.service";

/** An answer younger than this is reused; an older one is asked again on focus or when the module is entered. */
export const OTC_ACCESS_FRESH_MS = 15_000;

export interface AccessEntry {
  enabled: boolean | null;
  fetchedAt: number;
  /** The last read failed: the section stays closed, saying so, until a read answers. */
  failed?: boolean;
}

// One observable answer per member and organization, shared by the sidebar, the route guards and the module:
// a switch flipped by staff reaches them on the next focus or entry, without a reload.
const entries = new Map<string, AccessEntry>();
const inflight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export function subscribeOtcAccess(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function readOtcAccess(key: string): AccessEntry | undefined {
  return entries.get(key);
}

export function refreshOtcAccess(key: string, { force = false } = {}): Promise<void> {
  const entry = entries.get(key);
  if (!force && entry && entry.enabled !== null && Date.now() - entry.fetchedAt < OTC_ACCESS_FRESH_MS) {
    return Promise.resolve();
  }
  const running = inflight.get(key);
  if (running) return running;
  const request = CryptoSwapsService.access()
    .then((enabled) => {
      entries.set(key, { enabled, fetchedAt: Date.now() });
    })
    .catch(() => {
      // A failed read keeps the last answer; without one it is reported as a failure, never as "not enabled".
      const previous = entries.get(key);
      entries.set(key, {
        enabled: previous?.enabled ?? null,
        fetchedAt: 0,
        failed: previous?.enabled == null,
      });
    })
    .finally(() => {
      inflight.delete(key);
      notify();
    });
  inflight.set(key, request);
  return request;
}

/** Test seam: forget every answer. */
export function resetOtcAccess(): void {
  entries.clear();
  inflight.clear();
  notify();
}

/** Whether the signed-in user may buy and sell USDT. Staff always; members read false until answered. */
export function useOtcAccess(): {
  enabled: boolean;
  isLoading: boolean;
  hasError: boolean;
  retry: () => void;
} {
  const { user } = useAuth();
  const isStaff = user?.user_type === UserType.INTERNAL;
  const key = user && !isStaff ? `${user.id}:${user.organization_id ?? user.organization?.id ?? "-"}` : null;
  const entry = useSyncExternalStore(subscribeOtcAccess, () => (key ? readOtcAccess(key) : undefined));

  useEffect(() => {
    if (!key) return;
    void refreshOtcAccess(key);
    const revalidate = () => {
      if (document.visibilityState === "visible") void refreshOtcAccess(key);
    };
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    return () => {
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
    };
  }, [key]);

  const retry = () => {
    if (key) void refreshOtcAccess(key, { force: true });
  };
  if (isStaff) return { enabled: true, isLoading: false, hasError: false, retry };
  if (!key) return { enabled: false, isLoading: false, hasError: false, retry };
  if (entry?.failed) return { enabled: false, isLoading: false, hasError: true, retry };
  if (!entry || entry.enabled === null) return { enabled: false, isLoading: true, hasError: false, retry };
  return { enabled: entry.enabled, isLoading: false, hasError: false, retry };
}
